import { PRODUCT_TYPES } from "@shared/constants/paidFeaturesHierarchy";
import { PERMISSIONS, type Permission } from "@shared/constants/permissions";
import { Role } from "@shared/types/user";
import { isOemAdmin } from "@shared/utils/roleManager";

import type { UserType } from "~/types/user";

type PermissionUser = Pick<UserType, "_id" | "role" | "permissions">;
type RecordOwner = string | { _id?: string } | null | undefined;

const ownerId = (createdBy: RecordOwner) =>
  typeof createdBy === "string" ? createdBy : createdBy?._id;

const isOwnRecord = (
  user: Partial<PermissionUser> | null | undefined,
  createdBy: RecordOwner,
) => !!user?._id && ownerId(createdBy) === user._id;

/**
 * Whether the user's role holds a permission. `user.permissions` comes resolved
 * from the server for every role, system ones included.
 */
export const hasPermission = (
  user: Pick<UserType, "permissions"> | null | undefined,
  permission: Permission,
) => !!user?.permissions?.includes(permission);

/**
 * Whether the user may change a record. Update covers every record, whoever
 * created it; only deleting is limited to one's own (`canDeleteRecord`). The
 * creator is still taken so callers read the same as `canDeleteRecord`.
 */
export const canChangeRecord = (
  user: Partial<PermissionUser> | null | undefined,
  permission: Permission,
  _createdBy?: RecordOwner,
) => hasPermission(user, permission);

/**
 * Whether the user may remove a record where built-in roles delete any:
 * custom roles only remove records they created.
 */
export const canRemoveRecord = (
  user: Partial<PermissionUser> | null | undefined,
  permission: Permission,
  createdBy: RecordOwner,
) =>
  hasPermission(user, permission) &&
  (user?.role !== Role.CUSTOM || isOwnRecord(user, createdBy));

/** Whether the user may delete a record: Admins any, everyone else their own. */
export const canDeleteRecord = (
  user: Partial<PermissionUser> | null | undefined,
  permission: Permission,
  createdBy: RecordOwner,
) =>
  hasPermission(user, permission) &&
  (!!isOemAdmin(user?.role ?? "") || isOwnRecord(user, createdBy));

/** Whether the user has their own AI seat, which AI features need on top of the workspace's AI product. */
export const hasAiSeat = (
  user: Pick<UserType, "productAccess"> | null | undefined,
) => !!user?.productAccess?.includes(PRODUCT_TYPES.INDUSTRIAL_AI);

/**
 * Whether company inboxes show in the user's Emails page. Everyone has their
 * personal inbox; company ones need "Company Emails" (View), and Technicians
 * keep to their personal inbox. Company emails linked to a work order or
 * ticket show on that record regardless.
 */
export const hasCompanyInbox = (
  user: Pick<UserType, "role" | "permissions"> | null | undefined,
) =>
  user?.role !== Role.TECHNICIAN &&
  hasPermission(user, PERMISSIONS.emails.view);

/**
 * Whether the user may send from a company address on a work order, ticket,
 * quote or invoice. Custom roles need Create Company Emails; built-in roles
 * send from company addresses there as before.
 */
export const canSendFromCompanyOnRecord = (
  user: Pick<UserType, "role" | "permissions"> | null | undefined,
) =>
  user?.role !== Role.CUSTOM || hasPermission(user, PERMISSIONS.emails.create);
